// ⚠️ LICENSING: these are third-party brand logos. Confirm display/usage rights with each brand (or legal) BEFORE publishing this site publicly. Safe on localhost; do not ship without sign-off.
import React from 'react';
import { motion } from 'framer-motion';

/**
 * LogoWall - light theme customer/partner logo marquee.
 *
 * Full-color logos on an infinite horizontal auto-scroll (Cleo / OpenText
 * style), pausing on hover. The logo list is rendered TWICE so the loop is
 * seamless - the `marquee` keyframe (in index.css / tailwind.config) translates
 * the track by exactly -50%, i.e. one full copy of the list.
 *
 * Soft edge fades (left/right) let logos scroll in and out instead of hard-
 * cutting at the container edges.
 */
const LOGOS = [
  // Automotive OEMs
  { src: '/vendor-logos/ford.jpg', name: 'Ford' },
  { src: '/vendor-logos/generalmotors.jpg', name: 'General Motors' },
  { src: '/vendor-logos/toyota.jpg', name: 'Toyota' },
  { src: '/vendor-logos/honda.jpg', name: 'Honda' },
  { src: '/vendor-logos/bmw.jpg', name: 'BMW' },
  // Suppliers
  { src: '/vendor-logos/bosch.jpg', name: 'Bosch' },
  { src: '/vendor-logos/magna.jpg', name: 'Magna' },
  { src: '/vendor-logos/DensoNA.jpg', name: 'Denso' },
  { src: '/vendor-logos/yazaki.gif', name: 'Yazaki' },
  // Industrial
  { src: '/vendor-logos/caterpillar.jpg', name: 'Caterpillar' },
  { src: '/vendor-logos/johndeere.jpg', name: 'John Deere' },
  { src: '/vendor-logos/whirlpool.jpg', name: 'Whirlpool' },
  { src: '/vendor-logos/3m.jpg', name: '3M' },
  // Retail
  { src: '/vendor-logos/HomeDepot.jpg', name: 'The Home Depot' },
  { src: '/vendor-logos/lowes.jpg', name: "Lowe's" },
  { src: '/vendor-logos/bestbuy.jpg', name: 'Best Buy' },
  { src: '/vendor-logos/amazon.jpg', name: 'Amazon' },
];

// Rendered twice for a seamless loop; the second copy is decorative.
const LOOP = [...LOGOS, ...LOGOS];

const LogoWall = () => {
  return (
    <section className="relative py-12 lg:py-16 bg-bg-canvas overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0.5, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8 max-w-3xl xl:max-w-none mx-auto"
        >
          <p className="eyebrow">Trusted by</p>
          {/* xl:whitespace-nowrap keeps each line single on laptops/desktops at
              full size; below xl it wraps normally so phones never overflow. */}
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-text-primary mb-4 tracking-[-0.01em] text-balance xl:whitespace-nowrap">
            <span className="text-gradient-sky">Powering EDI</span>{' '}
            for the brands you know
          </h2>
          <p className="text-lg text-text-secondary text-pretty xl:whitespace-nowrap">
            From automotive OEMs to national retailers, teams rely on iConnect to keep
            their trading partners in sync.
          </p>
        </motion.div>
      </div>

      {/* Full-bleed marquee (spans wider than the container for a continuous feel) */}
      <div className="marquee-pause relative">
        {/* Soft edge fades so logos scroll in/out instead of hard-cutting */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-28 lg:w-40 z-10 bg-gradient-to-r from-white to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-28 lg:w-40 z-10 bg-gradient-to-l from-white to-transparent" />

        <div className="marquee-track py-2 motion-reduce:[animation-play-state:paused]">
          {LOOP.map((logo, idx) => (
            <div
              key={idx}
              className="flex items-center justify-center shrink-0"
              aria-hidden={idx >= LOGOS.length ? 'true' : undefined}
            >
              <img
                src={(process.env.PUBLIC_URL || '') + logo.src}
                alt={idx >= LOGOS.length ? '' : logo.name}
                loading="lazy"
                className="h-9 sm:h-10 lg:h-12 w-auto object-contain select-none"
                draggable="false"
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LogoWall;
